<template>
  <div class="team">
    <div class="profile__content__title">
      {{ $t("settings.team.title") }}
      <span v-if="members.length" class="team__count">{{
        members.length
      }}</span>
    </div>

    <div class="team__header" :class="{ 'team__header--scrolled': scrolled }">
      <p class="team__description">{{ $t("settings.team.description") }}</p>
      <el-input
        v-if="members.length > SEARCH_FROM"
        v-model="query"
        class="team__search"
        size="small"
        prefix-icon="el-icon-search"
        clearable
        :placeholder="$t('settings.team.search')"
      />
    </div>

    <div class="team__body">
      <div v-if="!members.length" class="team__empty">
        <span class="team__empty-icon"><i class="el-icon-user"></i></span>
        <p>{{ $t("settings.team.empty") }}</p>
      </div>

      <p v-else-if="!filtered.length" class="team__empty team__empty--search">
        {{ $t("settings.team.not_found") }}
      </p>

      <ul v-else ref="list" class="team__list" @scroll.passive="onListScroll">
        <li
          v-for="member in filtered"
          :key="member._id"
          class="drawer_users_in_project_item team__member"
          :class="{ 'team__member--pending': pending[member._id] }"
        >
          <div class="user_icon" :style="'background-color:' + member.color">
            <span
              v-if="$w_name_reduction(member.name) == $w_user_image(member)"
              v-html="$w_user_image(member)"
            ></span>
            <div v-else v-html="$w_user_image(member)"></div>
          </div>
          <div class="user_info">
            <div class="user_name">
              <span :title="member.name">{{ member.name || member.email }}</span>
            </div>
            <div
              v-if="showEmail(member)"
              class="user_email"
              :title="member.email"
            >
              {{ member.email }}
            </div>
          </div>
          <button
            type="button"
            class="team__remove"
            :disabled="!!pending[member._id]"
            @click="confirmRemove(member)"
          >
            <i v-if="pending[member._id]" class="el-icon-loading"></i>
            {{
              pending[member._id]
                ? $t("settings.team.removing")
                : $t("settings.team.remove")
            }}
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import { EventEmitter } from "../../../main";

// Сервер отвечает OUT_RemoveTeamMember; без ответа (обрыв сокета) кнопку разблокируем
export const REMOVE_TIMEOUT_MS = 20000;
// Для короткого списка поиск только мешает
export const SEARCH_FROM = 5;

const escapeHtml = (value) =>
  String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

export default {
  name: "SettingsTeam",
  data() {
    return {
      pending: {},
      query: "",
      scrolled: false,
      SEARCH_FROM,
    };
  },
  computed: {
    ...mapGetters(["getTeams"]),
    members() {
      return [...(this.getTeams || [])].sort((a, b) =>
        String(a.name || a.email || "").localeCompare(
          String(b.name || b.email || ""),
          undefined,
          { sensitivity: "base" }
        )
      );
    },
    filtered() {
      const query = this.query.trim().toLowerCase();
      if (!query) return this.members;
      return this.members.filter((member) =>
        [member.name, member.email].some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(query)
        )
      );
    },
  },
  watch: {
    // новая выдача поиска — начинаем сверху
    query() {
      this.scrolled = false;
      if (this.$refs.list) this.$refs.list.scrollTop = 0;
    },
  },
  created() {
    this.timers = {};
    EventEmitter.$on("RemoveTeamMemberResult", this.onRemoveResult);
  },
  beforeDestroy() {
    EventEmitter.$off("RemoveTeamMemberResult", this.onRemoveResult);
    Object.values(this.timers).forEach(clearTimeout);
  },
  methods: {
    ...mapMutations(["setMessage"]),
    onListScroll(e) {
      this.scrolled = e.target.scrollTop > 0;
    },
    showEmail(member) {
      return (
        !!member.email &&
        !!member.name &&
        member.name.trim().toLowerCase() != member.email.trim().toLowerCase()
      );
    },
    confirmRemove(member) {
      // Popup выводит text через v-html, а имя задаёт сам пользователь
      const name = escapeHtml(member.name || member.email);

      EventEmitter.$emit("OpenPopup", {
        type: "remove_team_member",
        data: {
          text:
            `<b>${this.$t("settings.team.confirm_title", { name })}</b><br>` +
            this.$t("settings.team.confirm_text", { name }),
          cancel: this.$t("app.alert.cancel"),
          accept: this.$t("settings.team.remove"),
          class: { accept: "red" },
          action: () => this.remove(member),
        },
      });
    },
    remove(member) {
      if (this.pending[member._id]) return;

      this.$set(this.pending, member._id, true);
      this.$socket.emit("IN_RemoveTeamMember", { user_id: member._id });

      // Единственный текст, который остаётся на клиенте: ответа не пришло,
      // значит и сообщения от сервера не будет
      this.timers[member._id] = setTimeout(() => {
        this.finish(member._id);
        this.setMessage({
          key_message: "warning",
          message: this.$t("settings.team.messages.error"),
        });
      }, REMOVE_TIMEOUT_MS);
    },
    // Текст статуса шлёт сервер (OUT_Message), здесь только снимаем блокировку
    onRemoveResult(data) {
      if (!data || !this.pending[data.user_id]) return;

      this.finish(data.user_id);
    },
    finish(user_id) {
      clearTimeout(this.timers[user_id]);
      delete this.timers[user_id];
      this.$delete(this.pending, user_id);
    },
  },
};
</script>

<style lang="scss" scoped>
// Отступы и цвета — как у соседних разделов Settings (settings-polish.scss),
// цвета через токены --color-*, чтобы тёмная тема (dark-theme.scss) работала сама.
$gutter: 28px;

.team {
  --team-danger: #df5962;
  --team-danger-bg: rgba(223, 89, 98, 0.1);
  --team-header-shadow: rgba(31, 35, 40, 0.12);

  display: flex;
  flex-direction: column;
  height: 100%;
  font-family: var(--font-family-body);
}

body.dark-mode .team {
  --team-danger: #ff777f;
  --team-danger-bg: rgba(223, 89, 98, 0.15);
  --team-header-shadow: rgba(0, 0, 0, 0.45);
}

// у span в .profile__content__title есть глобальные width/height/фон — перебиваем
.team .team__count {
  display: inline-block;
  width: auto;
  height: auto;
  margin: 0 0 0 8px;
  padding: 1px 8px;
  border-radius: 10px;
  background-color: var(--color-surface-hover);
  color: var(--color-text-secondary);
  font-family: var(--font-family-body);
  font-size: var(--font-size-sm);
  font-weight: 500;
  line-height: 18px;
  cursor: default;
}

.team__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  position: relative;
  z-index: 1;
  padding: 22px $gutter 16px;
  transition: box-shadow 0.15s;

  // список прокручен — отделяем шапку, чтобы строки не обрезались «в никуда»
  &--scrolled {
    box-shadow: 0 6px 10px -8px var(--team-header-shadow);
  }
}

.team__description {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-md);
  line-height: var(--line-height-normal);
}

.team__search {
  flex: 0 0 240px;

  ::v-deep .el-input__inner {
    height: 36px;
    border-color: var(--color-border);
    border-radius: var(--radius-md);
    color: var(--color-text-primary);
    background-color: var(--color-surface);
    font-family: var(--font-family-body);
    box-shadow: none;

    &:focus {
      border-color: color-mix(in srgb, var(--theme-color) 60%, var(--color-border));
      box-shadow: 0 0 0 2px color-mix(in srgb, var(--theme-color) 10%, transparent);
    }
  }
}

.team__body {
  flex: 1;
  min-height: 0;
  margin: 0 $gutter 24px;
  overflow: hidden;
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  background-color: var(--color-surface);
}

.team__empty {
  height: 100%;
  min-height: 220px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 40px $gutter;
  color: var(--color-text-muted);
  font-size: var(--font-size-md);
  text-align: center;

  .team__empty-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    color: var(--color-text-muted);
    background-color: var(--color-surface-subtle);

    i {
      font-size: 24px;
    }
  }

  p {
    margin: 0;
  }

  &--search {
    min-height: 150px;
  }
}

.team__list {
  flex: 1;
  min-height: 0;
  margin: 0;
  height: 100%;
  box-sizing: border-box;
  padding: 4px 10px 12px;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--color-border) transparent;

  &::-webkit-scrollbar {
    width: 6px;
    height: 0;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 3px;
    background-color: var(--color-border);
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
}

.team__member {
  // глобальный .drawer_users_in_project_item задаёт width: 100% — без border-box
  // паддинги выталкивают строку за контейнер и дают горизонтальный скролл
  box-sizing: border-box;
  min-height: 58px;
  margin: 0;
  padding: 10px 12px;
  border-bottom: 1px solid var(--color-border-subtle);
  border-radius: 0;
  background-color: transparent;
  transition: background-color 0.12s ease;

  &:hover {
    background-color: var(--color-surface-hover);
  }

  &:last-child {
    border-bottom: 0;
  }

  .user_icon {
    flex-shrink: 0;
    width: 38px;
    height: 38px;
    margin-right: 14px;
    font-size: 15px;
  }

  .user_info {
    flex: 1;
    min-width: 0;
    gap: 2px;
  }

  .user_name {
    max-width: 100%;
    color: var(--color-text-primary);
    font-size: var(--font-size-base);
    font-weight: 500;

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .user_email {
    max-width: 100%;
    overflow: hidden;
    color: var(--color-text-muted);
    font-size: var(--font-size-sm);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &--pending {
    opacity: 0.6;
  }
}

.team__remove {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  margin-left: 12px;
  padding: 0 12px;
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--color-text-secondary);
  font-size: var(--font-size-md);
  font-family: inherit;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.12s ease, color 0.12s ease, background-color 0.12s ease;

  .team__member:hover &,
  &:focus-visible,
  &:disabled {
    opacity: 1;
  }

  &:hover:not(:disabled),
  &:focus-visible {
    color: var(--team-danger);
    background-color: var(--team-danger-bg);
    border-color: color-mix(in srgb, var(--team-danger) 22%, transparent);
    outline: none;
  }

  &:disabled {
    cursor: default;
  }

  // без мыши наведения нет — кнопка видна всегда
  @media (hover: none) {
    opacity: 1;
  }
}
</style>
